// Builds the HTML page for the PDF report export. Pure: no window, no DOM.
// Every value that comes from data or from the report is escaped, so a customer or job named
// like "<img src=x onerror=...>" prints as plain text instead of running as code.
// The page has no script at all (the browser security policy blocks inline scripts); the app opens the
// print dialog itself with printWhenReady below.
import { escapeHtml, } from "./escapeHtml";
import { toWholeCents } from "./money";
import { pdfNoteHTML } from "./reportMonthly";
import { isSafeLogoDataUrl } from "./quoteLogo";
import { formatIsoLong, formatPercentText } from "./clientQuote";

// One money formatter for every export (report PDF, quote PDF).
// A loss prints with the minus sign before the dollar sign ("-$8,038.13"), with thousands separators.
// Rounds to the cent first, so a value that rounds to zero never prints as "-$0.00".
// showCents: true = always 2 decimals, false = whole dollars, undefined = 2 decimals only if this value has cents.
export { toWholeCents };
export function hasCents(v) {
  return typeof v === "number" && Number.isFinite(v) && toWholeCents(v) % 100 !== 0;
}
export function formatExportMoney(v, showCents) {
  const n = Number(v);
  if (!Number.isFinite(n)) return showCents ? "$0.00" : "$0";
  const cents = toWholeCents(n);
  const withCents = showCents === undefined ? cents % 100 !== 0 : showCents;
  const digits = withCents ? 2 : 0;
  const body = (Math.abs(cents) / 100).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return `${cents < 0 ? "-" : ""}$${body}`;
}

// Number formatting for report table cells. Money vs percent vs count is decided by the column key:
// "margin" is a percent (keeps its sign), "jobs" is a count, every other number is money.
// showCents is the column-wide choice from buildPrintHTML so a column never mixes "$100" and "$100.50".
// A missing value (for example, no margin because there is no revenue) prints as a dash.
export function formatPdfValue(k, v, showCents) {
  if (v === null || v === undefined) return "—";
  if (typeof v !== "number") return v;
  if (k === "margin") return `${v}%`;
  if (k === "jobs") return v;
  return formatExportMoney(v, showCents);
}

// Quantity or hours as a number ("1,200", "2.5"). Text that is not a plain number stays as written.
export function formatQuantity(q) {
  if (q === undefined || q === null || String(q).trim() === "") return "—";
  const n = typeof q === "number" ? q : (/^\s*-?\d+(\.\d+)?\s*$/.test(String(q)) ? Number(q) : NaN);
  if (!Number.isFinite(n)) return q;
  return n.toLocaleString("en-US", { maximumFractionDigits: 6 });
}

// Margin percent with one decimal, like the estimator on screen ("-400.0").
function formatMarginPct(m) {
  const n = parseFloat(m);
  return Number.isFinite(n) ? n.toFixed(1) : "0.0";
}

const PRINT_CSS = `
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; background: white; color: #2C2416; padding: 32px 40px; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #DDD5C4; }
    .logo { font-size: 18px; font-weight: bold; color: #1A3C2E; }
    .logo span { font-size: 10px; display: block; color: #A89880; letter-spacing: 0.1em; text-transform: uppercase; margin-top: 2px; font-weight: normal; }
    .date { font-size: 11px; color: #A89880; text-align: right; }
    h1 { font-size: 20px; font-weight: bold; color: #2C2416; margin-bottom: 4px; }
    .desc { font-size: 12px; color: #6B5E4E; margin-bottom: 20px; }
    .insight { background: #F0F5EF; border: 1px solid rgba(92,122,90,0.3); border-left: 4px solid #2D6A4F; border-radius: 4px; padding: 14px 16px; margin-bottom: 24px; }
    .insight-label { font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; color: #2D6A4F; font-weight: bold; margin-bottom: 6px; }
    .note { font-size: 11px; color: #6B5E4E; margin: -12px 0 20px; }
    .insight-text { font-size: 12px; color: #6B5E4E; line-height: 1.7; }
    .section-label { font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; color: #A89880; font-weight: bold; margin-bottom: 10px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { background: #F5F0E8; padding: 8px 12px; text-align: left; font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: #A89880; border-bottom: 1px solid #DDD5C4; font-weight: 500; }
    td { padding: 10px 12px; border-bottom: 1px solid #EDE8DC; color: #6B5E4E; }
    tr:last-child td { border-bottom: none; }
    td:not(:first-child) { font-family: 'Courier New', monospace; }
    .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #DDD5C4; font-size: 10px; color: #A89880; display: flex; justify-content: space-between; }
    @media print { @page { margin: 1cm; size: A4 landscape; } }
`;

export function buildPrintHTML({ title, description, insight, data, note, date = new Date() }) {
  const rows = Array.isArray(data) ? data : [];
  const headers = Object.keys(rows[0] || {});

  // Per money column: 2 decimals for every row if any row has cents, otherwise whole dollars.
  const centsByKey = {};
  headers.forEach(k => {
    centsByKey[k] = rows.some(r => k !== "margin" && k !== "jobs" && hasCents(r[k]));
  });

  const tableRows = rows.map(row =>
    `<tr>${headers.map(k => `<td>${escapeHtml(formatPdfValue(k, row[k], centsByKey[k]))}</td>`).join("")}</tr>`
  ).join("");

  const tableHTML = `
    <table>
      <thead><tr>${headers.map(k => `<th>${escapeHtml(k.charAt(0).toUpperCase() + k.slice(1))}</th>`).join("")}</tr></thead>
      <tbody>${tableRows}</tbody>
    </table>
  `;

  const dateText = date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Canopy — ${escapeHtml(title)}</title>
  <style>${PRINT_CSS}  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="logo">Canopy <span>Business Intelligence</span></div>
    </div>
    <div class="date">
      ${dateText}
    </div>
  </div>

  <h1>${escapeHtml(title)}</h1>
  <div class="desc">${escapeHtml(description)}</div>

  <div class="insight">
    <div class="insight-label">Canopy Insight</div>
    <div class="insight-text">${escapeHtml(insight)}</div>
  </div>

  ${pdfNoteHTML(note)}

  <div class="section-label">Data</div>
  ${tableHTML}

  <div class="footer">
    <span>Canopy Business Intelligence · app.canopybi.com</span>
    <span>Confidential — for internal use only</span>
  </div>

</body>
</html>`;
}

// Builds the HTML page for the quote PDF. Pure: no window, no DOM.
// Every value from the estimate (names, line items, notes, totals) is escaped. Notes keep their
// line breaks through the CSS rule white-space: pre-wrap, so no newline conversion is needed.
// getLineAmount is passed in so this module does not depend on App.js.
export function buildQuoteHTML({ name, jobType, expectedRevenue, costLines, notes, contractorName, totalCosts, grossProfit, grossMargin, getLineAmount, date = new Date() }) {
  const money = (n) => formatExportMoney(n, true);
  const rev = parseFloat(expectedRevenue) || 0;
  const lineRows = (costLines || []).filter(l => getLineAmount(l) !== 0).map(l => { // credit (negative) lines stay, so the lines add up to the total
    const amt = getLineAmount(l);
    return `<tr>
      <td>${escapeHtml(l.description || '—')}</td>
      <td>${escapeHtml(l.category || '—')}</td>
      <td class="mono right">${escapeHtml(formatQuantity(l.hours || l.quantity))}</td>
      <td class="mono right">${escapeHtml(money(amt))}</td>
    </tr>`;
  }).join('');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Quote — ${escapeHtml(name || 'Untitled')}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; background: white; color: #2C2416; padding: 40px 48px; max-width: 800px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 32px; padding-bottom: 20px; border-bottom: 2px solid #DDD5C4; }
    .company { font-size: 20px; font-weight: 700; color: #1E1810; letter-spacing: -0.02em; }
    .company-sub { font-size: 11px; color: #9C8A74; margin-top: 4px; }
    .meta { text-align: right; font-size: 11px; color: #9C8A74; line-height: 1.8; }
    .title { font-size: 22px; font-weight: 700; color: #2C2416; margin-bottom: 6px; }
    .subtitle { font-size: 12px; color: #6B5E4E; margin-bottom: 28px; }
    .section-label { font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; color: #9C8A74; font-weight: 700; margin-bottom: 10px; margin-top: 24px; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; margin-bottom: 0; }
    th { background: #F5F0E8; padding: 8px 12px; text-align: left; font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: #9C8A74; border-bottom: 1px solid #DDD5C4; font-weight: 600; }
    th.right { text-align: right; }
    td { padding: 10px 12px; border-bottom: 1px solid #EDE8DC; color: #4A3F32; }
    td.mono { font-family: 'Courier New', monospace; }
    td.right, th.right { text-align: right; }
    tr:last-child td { border-bottom: none; }
    .totals { margin-top: 2px; border-top: 2px solid #DDD5C4; padding: 16px 0; }
    .total-row { display: flex; justify-content: space-between; padding: 6px 12px; font-size: 13px; }
    .total-row.highlight { font-weight: 700; font-size: 15px; color: #2C2416; margin-top: 8px; padding-top: 10px; border-top: 1px solid #DDD5C4; }
    .total-row .label { color: #6B5E4E; }
    .total-row .value { font-family: 'Courier New', monospace; color: #2C2416; }
    .notes { margin-top: 28px; padding: 16px 18px; background: #FAF8F4; border: 1px solid #EDE8DC; border-radius: 4px; }
    .notes-label { font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: #9C8A74; font-weight: 700; margin-bottom: 8px; }
    .notes-text { font-size: 12px; color: #6B5E4E; line-height: 1.7; white-space: pre-wrap; }
    .footer { margin-top: 40px; padding-top: 12px; border-top: 1px solid #DDD5C4; font-size: 10px; color: #9C8A74; text-align: center; }
    @media print { @page { margin: 1.5cm; size: A4 portrait; } body { padding: 0; } }
  </style>
</head>
<body>
  <div class="header">
    <div>
      <div class="company">${escapeHtml(contractorName || 'Quote')}</div>
      <div class="company-sub">Project Estimate</div>
    </div>
    <div class="meta">
      ${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}<br/>
      ${jobType ? `Type: ${escapeHtml(jobType)}` : ''}
    </div>
  </div>

  <div class="title">${escapeHtml(name || 'Untitled Estimate')}</div>
  <div class="subtitle">Detailed cost breakdown and projected pricing</div>

  <div class="section-label">Line Items</div>
  <table>
    <thead><tr><th>Description</th><th>Category</th><th class="right">Qty / Hrs</th><th class="right">Amount</th></tr></thead>
    <tbody>${lineRows || '<tr><td colspan="4" style="text-align:center;color:#9C8A74;padding:20px;">No line items</td></tr>'}</tbody>
  </table>

  <div class="totals">
    <div class="total-row"><span class="label">Total Estimated Costs</span><span class="value">${escapeHtml(money(totalCosts))}</span></div>
    ${rev > 0 ? `<div class="total-row"><span class="label">Quoted Price</span><span class="value">${escapeHtml(money(rev))}</span></div>` : ''}
    ${rev > 0 ? `<div class="total-row highlight"><span class="label">Projected Gross Profit</span><span class="value">${escapeHtml(`${money(grossProfit)} (${formatMarginPct(grossMargin)}%)`)}</span></div>` : ''}
  </div>

  ${notes ? `<div class="notes"><div class="notes-label">Notes</div><div class="notes-text">${escapeHtml(notes)}</div></div>` : ''}

  <div class="footer">
    Prepared by ${escapeHtml(contractorName || '—')} · ${date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
  </div>

</body>
</html>`;
}

// Builds the HTML page for the CLIENT version of a quote: what an owner sends to their customer. Pure.
// It is given only what the client may see: client prices, never cost, markup, profit or margin. The caller
// passes the lines already priced (see clientQuoteTotals in src/clientQuote.js); this function adds nothing up
// itself except to print the numbers it was given, so the page always shows exactly the app's totals.
// lines: [{ description, quantity (text, only for unit lines, else ""), priceCents }]
// logoDataUrl is printed only if it is a safe PNG or JPEG data URL (src/quoteLogo.js); the page has no script
// and loads nothing from the network (the logo is a data: image, which the site's security policy allows).
export function buildClientQuoteHTML({ name, businessName, logoDataUrl, lines, subtotalCents, taxOn, taxRate, taxCents, totalCents, clientNotes, validUntil, date = new Date() }) {
  const money = (cents) => formatExportMoney(cents / 100, true);
  const shown = (lines || []).filter(l => l.priceCents !== 0); // credit (negative) lines stay, so the lines add up
  const showQty = shown.some(l => String(l.quantity || '').trim() !== '');
  const rows = shown.map(l => `<tr>
      <td>${escapeHtml(l.description || '—')}</td>
      ${showQty ? `<td class="mono right">${escapeHtml(String(l.quantity || '').trim() === '' ? '' : formatQuantity(l.quantity))}</td>` : ''}
      <td class="mono right">${escapeHtml(money(l.priceCents))}</td>
    </tr>`).join('');
  const cols = showQty ? 3 : 2;
  const rate = taxOn ? formatPercentText(taxRate) : '';
  const validText = formatIsoLong(validUntil);
  const logo = isSafeLogoDataUrl(logoDataUrl) ? `<img class="logo-img" src="${logoDataUrl}" alt=""/>` : '';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Quote — ${escapeHtml(name || 'Untitled')}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; background: white; color: #2C2416; padding: 40px 48px; max-width: 800px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; margin-bottom: 32px; padding-bottom: 20px; border-bottom: 2px solid #DDD5C4; }
    .brand { display: flex; align-items: center; gap: 16px; }
    .logo-img { max-height: 64px; max-width: 200px; object-fit: contain; }
    .company { font-size: 22px; font-weight: 700; color: #1E1810; letter-spacing: -0.02em; }
    .meta { text-align: right; font-size: 12px; color: #6B5E4E; line-height: 1.8; white-space: nowrap; }
    .meta b { color: #2C2416; }
    .title { font-size: 22px; font-weight: 700; color: #2C2416; margin-bottom: 24px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th { background: #F5F0E8; padding: 8px 12px; text-align: left; font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; color: #6B5E4E; border-bottom: 1px solid #DDD5C4; font-weight: 600; }
    th.right { text-align: right; }
    td { padding: 10px 12px; border-bottom: 1px solid #EDE8DC; color: #2C2416; vertical-align: top; }
    td.mono { font-family: 'Courier New', monospace; }
    td.right, th.right { text-align: right; }
    tr { page-break-inside: avoid; }
    .totals { margin-top: 8px; padding: 8px 0; page-break-inside: avoid; }
    .total-row { display: flex; justify-content: space-between; padding: 6px 12px; font-size: 14px; }
    .total-row .label { color: #6B5E4E; }
    .total-row .value { font-family: 'Courier New', monospace; color: #2C2416; }
    .total-row.grand { font-weight: 700; font-size: 17px; margin-top: 8px; padding-top: 12px; border-top: 2px solid #DDD5C4; }
    .total-row.grand .label { color: #2C2416; }
    .notes { margin-top: 28px; padding: 16px 18px; background: #FAF8F4; border: 1px solid #EDE8DC; border-radius: 4px; page-break-inside: avoid; }
    .notes-label { font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: #6B5E4E; font-weight: 700; margin-bottom: 8px; }
    .notes-text { font-size: 12px; color: #4A3F32; line-height: 1.7; white-space: pre-wrap; }
    .footer { margin-top: 40px; padding-top: 12px; border-top: 1px solid #DDD5C4; font-size: 11px; color: #6B5E4E; text-align: center; }
    @media print { @page { margin: 1.5cm; size: A4 portrait; } body { padding: 0; } }
    @media (max-width: 480px) { body { padding: 20px 16px; } .header { flex-direction: column; } .meta { text-align: left; } }
  </style>
</head>
<body>
  <div class="header">
    <div class="brand">
      ${logo}
      <div class="company">${escapeHtml(businessName || 'Quote')}</div>
    </div>
    <div class="meta">
      <div><b>Quote date:</b> ${escapeHtml(date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }))}</div>
      ${validText ? `<div><b>Valid until:</b> ${escapeHtml(validText)}</div>` : ''}
    </div>
  </div>

  <div class="title">${escapeHtml(name || 'Untitled quote')}</div>

  <table>
    <thead><tr><th>Description</th>${showQty ? '<th class="right">Quantity</th>' : ''}<th class="right">Price</th></tr></thead>
    <tbody>${rows || `<tr><td colspan="${cols}" style="text-align:center;color:#6B5E4E;padding:20px;">No line items</td></tr>`}</tbody>
  </table>

  <div class="totals">
    <div class="total-row"><span class="label">Subtotal</span><span class="value">${escapeHtml(money(subtotalCents))}</span></div>
    ${taxOn ? `<div class="total-row"><span class="label">Sales tax${rate ? ` (${escapeHtml(rate)}%)` : ''}</span><span class="value">${escapeHtml(money(taxCents))}</span></div>` : ''}
    <div class="total-row grand"><span class="label">Total</span><span class="value">${escapeHtml(money(totalCents))}</span></div>
  </div>

  ${clientNotes ? `<div class="notes"><div class="notes-label">Notes and terms</div><div class="notes-text">${escapeHtml(clientNotes)}</div></div>` : ''}

  <div class="footer">Prepared by ${escapeHtml(businessName || '—')}</div>

</body>
</html>`;
}

// Opens the print dialog for a window we wrote a report or quote into. Called right after document.close(),
// in the same click, so phones keep the user's tap and the dialog opens (a timer in this tab can be paused
// on iPhone and iPad). The pages have no images or web fonts to wait for. The page itself has no script, so
// the site-wide security policy never needs to allow inline scripts.
export function printWhenReady(win) {
  try { win.focus(); win.print(); } catch { /* window was closed */ }
}
